import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { IconSize } from "shared/types/Icon"
import { Button, ButtonProps } from "shared/ui/buttons/Button"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import "./styles/_hint.scss"

export type HintVariant = "default" | "warning" | "danger"

interface HintRootProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
  variant?: HintVariant
}

interface HintIconProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  name: IconName
  size?: IconSize
}

interface HintContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface HintTitleProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface HintTextProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface HintSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface HintSectionHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface HintCloseProps extends Omit<ButtonProps, "icon" | "isIconOnly"> {
  icon?: IconName
}

const Root = React.memo(
  React.forwardRef<HTMLDivElement, HintRootProps>(
    ({ children, className, variant = "default", ...props }, ref) => {
      return (
        <div
          {...props}
          ref={ref}
          className={classNames(
            "hint",
            {
              [`hint--${variant}`]: variant !== "default"
            },
            className
          )}
          role="status"
          aria-live="polite"
        >
          {children}
        </div>
      )
    }
  )
)
Root.displayName = "HintRoot"

const HintIcon = React.memo(
  React.forwardRef<HTMLDivElement, HintIconProps>(
    ({ name, size = IconSize.XS, className, ...props }, ref) => {
      return (
        <div
          {...props}
          ref={ref}
          className={classNames("hint__icon", className)}
          aria-hidden="true"
        >
          <Icon name={name} size={size} />
        </div>
      )
    }
  )
)
HintIcon.displayName = "HintIcon"

const Content = React.memo(
  React.forwardRef<HTMLDivElement, HintContentProps>(
    ({ children, className, ...props }, ref) => {
      return (
        <div
          {...props}
          ref={ref}
          className={classNames("hint__content", className)}
        >
          {children}
        </div>
      )
    }
  )
)
Content.displayName = "HintContent"

const Title = React.memo(
  React.forwardRef<HTMLDivElement, HintTitleProps>(
    ({ children, className, ...props }, ref) => {
      return (
        <div
          {...props}
          ref={ref}
          className={classNames("hint__title", className)}
        >
          {children}
        </div>
      )
    }
  )
)
Title.displayName = "HintTitle"

const Text = React.memo(
  React.forwardRef<HTMLDivElement, HintTextProps>(
    ({ children, className, ...props }, ref) => {
      return (
        <div
          {...props}
          ref={ref}
          className={classNames("hint__text", className)}
        >
          {children}
        </div>
      )
    }
  )
)
Text.displayName = "HintText"

const Section = React.memo(
  React.forwardRef<HTMLDivElement, HintSectionProps>(
    ({ children, className, ...props }, ref) => {
      return (
        <div
          {...props}
          ref={ref}
          className={classNames("hint__section", className)}
        >
          {children}
        </div>
      )
    }
  )
)
Section.displayName = "HintSection"

const SectionHeader = React.memo(
  React.forwardRef<HTMLDivElement, HintSectionHeaderProps>(
    ({ children, className, ...props }, ref) => {
      return (
        <div
          {...props}
          ref={ref}
          className={classNames("hint__section-header", className)}
        >
          {children}
        </div>
      )
    }
  )
)
SectionHeader.displayName = "HintSectionHeader"

const Close = React.memo(
  React.forwardRef<HTMLButtonElement, HintCloseProps>(
    ({ className, icon = IconName.CLOSE, label, ...props }, ref) => {
      const { t } = useTranslation()

      return (
        <Button
          {...props}
          ref={ref}
          className={classNames("btn--borderless", "hint__close", className)}
          icon={icon}
          iconSize={IconSize.XS}
          isIconOnly
          // Icon-only buttons have no text node, so Button's label -> aria-label
          // forwarding is the only accessible name available.
          label={label || t("General.Close")}
          variant="secondary"
        />
      )
    }
  )
)
Close.displayName = "HintClose"

const Hint = {
  Root,
  Icon: HintIcon,
  Content,
  Title,
  Section,
  SectionHeader,
  Text,
  Close
}

export { Hint }
